/**
 * Admin Auction Configuration Panel
 * Allows admin to configure auction settings
 */
import { useState, useEffect } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { formatBYC, AUCTION_STATUS_COLORS, getErrorMessage } from './useAuction';
import { copyText } from '../../utils/copyText';

// Toast reflects whether the copy actually happened
async function copyWithToast(text, what) {
  if (await copyText(text)) toast.success(`${what} copied`);
  else toast.error(`Copy failed — select the ${what.toLowerCase()} and copy it`);
}

// ─── Auctioneer Credentials Section ────────────────────────────────────────────
function AuctioneerCredentialsSection({ eventId }) {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [sending, setSending] = useState(false);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/auctioneer/credentials`);
      setCredentials(res.data);
    } catch (e) {
      // 404 means no credentials yet
      if (e.response?.status !== 404) {
        console.error('Failed to fetch auctioneer credentials:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateCredentials = async () => {
    setGenerating(true);
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/auction/auctioneer/generate`);
      setCredentials(res.data);
      toast.success('Auctioneer credentials generated!');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to generate credentials'));
    } finally {
      setGenerating(false);
    }
  };

  const regenerateCredentials = async () => {
    if (!window.confirm('This will invalidate the current credentials. Are you sure?')) return;
    setGenerating(true);
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/auction/auctioneer/regenerate`);
      setCredentials(res.data);
      toast.success('Auctioneer credentials regenerated!');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to regenerate credentials'));
    } finally {
      setGenerating(false);
    }
  };

  const sendCredentialsEmail = async () => {
    if (!emailInput.trim() || !emailInput.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!window.confirm('This emails a NEW password. The current password will stop working once the email is sent. Continue?')) return;
    setSending(true);
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/auction/auctioneer/send-credentials`, {
        email: emailInput.trim(),
      });
      if (res.data?.password) setCredentials(res.data);
      toast.success(`Credentials sent to ${emailInput.trim()}`);
      setEmailInput('');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send email'));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, [eventId]);

  return (
    <div className="bg-white rounded-xl p-5 border border-amber-200 shadow-sm" data-testid="auctioneer-credentials-section">
      <h4 className="font-bold mb-4 flex items-center gap-2">
        <i className="ph-bold ph-user-circle-gear text-amber-600"></i>
        Auctioneer Credentials
      </h4>
      <p className="text-sm text-gray-500 mb-4">
        Share these credentials with the person operating the live auction. They will have limited access to:
        select players, enter bids, and mark players as sold/unsold.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
        </div>
      ) : credentials ? (
        <div className="space-y-4">
          {/* Credentials display */}
          <div className="bg-amber-50 rounded-lg p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Username</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border border-amber-200 font-mono text-lg font-bold tracking-wider">
                  {credentials.username}
                </code>
                <button
                  onClick={() => copyWithToast(credentials.username, 'Username')}
                  className="px-3 py-2 bg-amber-100 rounded hover:bg-amber-200 transition-colors"
                  title="Copy username"
                >
                  <i className="ph-bold ph-copy"></i>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Password</label>
              {!credentials.password ? (
                <p className="text-sm text-gray-600 bg-white px-3 py-2 rounded border border-amber-200" data-testid="auctioneer-password-hidden">
                  <i className="ph-bold ph-lock-simple mr-1"></i>
                  Hidden for security — it was shown once when created. If it's lost, regenerate it or email a new one below.
                </p>
              ) : (
              <>
              <p className="text-xs font-bold text-red-600 mb-1" data-testid="auctioneer-password-once">
                <i className="ph-bold ph-warning mr-1"></i>
                Copy this password now — it won't be shown again.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border border-amber-200 font-mono text-lg font-bold tracking-wider">
                  {showPassword ? credentials.password : '••••••••'}
                </code>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="px-3 py-2 bg-amber-100 rounded hover:bg-amber-200 transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`ph-bold ${showPassword ? 'ph-eye-slash' : 'ph-eye'}`}></i>
                </button>
                <button
                  onClick={() => copyWithToast(credentials.password, 'Password')}
                  className="px-3 py-2 bg-amber-100 rounded hover:bg-amber-200 transition-colors"
                  title="Copy password"
                >
                  <i className="ph-bold ph-copy"></i>
                </button>
              </div>
              </>
              )}
            </div>
            {credentials.created_at && (
              <p className="text-xs text-gray-400">
                Generated: {new Date(credentials.created_at).toLocaleString()}
              </p>
            )}
          </div>

          {/* Send via Email */}
          <div className="border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <i className="ph-bold ph-envelope text-[#3AAFA9]"></i>
              Send Credentials via Email
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendCredentialsEmail()}
                placeholder="auctioneer@example.com"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none"
                data-testid="auctioneer-email-input"
              />
              <button
                onClick={sendCredentialsEmail}
                disabled={sending || !emailInput.trim()}
                className="px-4 py-2 bg-[#3AAFA9] text-white rounded-lg font-bold text-sm hover:bg-[#2d8a85] disabled:opacity-50 transition-colors"
                data-testid="send-credentials-btn"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              <i className="ph-bold ph-info mr-1"></i>
              Sends a new password (the current one stops working). Credentials expire when the auction is closed.
            </p>
          </div>

          <button
            onClick={regenerateCredentials}
            disabled={generating}
            className="w-full py-2 rounded-lg border border-amber-300 text-amber-700 font-medium hover:bg-amber-50 transition-colors disabled:opacity-50"
            data-testid="regenerate-auctioneer-btn"
          >
            {generating ? 'Regenerating...' : 'Regenerate Credentials'}
          </button>
          <p className="text-xs text-amber-600">
            <i className="ph-bold ph-warning mr-1"></i>
            Regenerating will invalidate the current credentials immediately.
          </p>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-gray-500 mb-3">No auctioneer credentials generated yet.</p>
          <button
            onClick={generateCredentials}
            disabled={generating}
            className="px-6 py-3 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors disabled:opacity-50"
            data-testid="generate-auctioneer-btn"
          >
            {generating ? 'Generating...' : 'Generate Auctioneer Credentials'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AuctionConfigPanel({ eventId, config, teams = [], onConfigChange }) {
  const [formData, setFormData] = useState({
    total_teams: 6,
    squad_size: 15,
    minimum_squad_size: 11,
    initial_wallet_byc: 10000000,
    standard_base_price_byc: 100000,
    bid_increment_type: 'FIXED',
    bid_increment_value: 50000,
    enable_max_bid_cap: true,
    enable_age_constraint: false,
    age_threshold: 19,
    minimum_age_count: 2,
    enable_owner_participation: false,
    owner_flat_price_byc: 0,
    enable_icon_players: false,
    icon_player_fixed_price_byc: 0,
    enable_bid_timer: false,
    bid_timer_seconds: 30,
    enable_unsold_round: true,
  });
  
  const [slabs, setSlabs] = useState([
    { min_amount: 0, max_amount: 500000, increment: 50000 },
    { min_amount: 500000, max_amount: 1000000, increment: 100000 },
  ]);
  
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [eventDefaults, setEventDefaults] = useState(null);
  const [poolStats, setPoolStats] = useState(null);
  const isLocked = config?.locked_by_admin;
  // Config is editable if: no config exists (create new), OR config is DRAFT and not locked
  const isEditable = !config || (!isLocked && config?.auction_status === 'DRAFT');

  // Fetch event defaults when no config exists
  useEffect(() => {
    const fetchDefaults = async () => {
      if (config || defaultsLoaded) return; // Skip if config exists or already loaded
      
      try {
        const res = await axiosInstance.get(`/v1/events/${eventId}/auction/config/defaults`);
        const defaults = res.data;
        setEventDefaults(defaults);
        
        // Pre-populate form with inherited values
        setFormData(prev => ({
          ...prev,
          total_teams: defaults.total_teams || prev.total_teams,
          squad_size: defaults.squad_size || prev.squad_size,
          minimum_squad_size: defaults.minimum_squad_size || prev.minimum_squad_size,
        }));
        
        if (defaults.source === 'event_config') {
          toast.info('Team & Squad settings inherited from event configuration', {
            duration: 4000,
            icon: '✓'
          });
        }
        setDefaultsLoaded(true);
      } catch (err) {
        // Silently ignore - will use hardcoded defaults
        console.log('Could not fetch event defaults:', err);
        setDefaultsLoaded(true);
      }
    };
    
    fetchDefaults();
  }, [eventId, config, defaultsLoaded]);

  // Fetch pool stats to gate Lock & Submit
  useEffect(() => {
    if (!config) return;
    axiosInstance.get(`/v1/events/${eventId}/auction/pool/stats`)
      .then(res => setPoolStats(res.data))
      .catch(() => setPoolStats(null));
  }, [eventId, config?.id]);

  // Load existing config values
  useEffect(() => {
    if (config) {
      setFormData({
        total_teams: config.total_teams || 6,
        squad_size: config.squad_size || 15,
        minimum_squad_size: config.minimum_squad_size || 11,
        initial_wallet_byc: config.initial_wallet_byc || 10000000,
        standard_base_price_byc: config.standard_base_price_byc || 100000,
        bid_increment_type: config.bid_increment_type || 'FIXED',
        bid_increment_value: config.bid_increment_type === 'FIXED' ? config.bid_increment_value : 50000,
        enable_max_bid_cap: config.enable_max_bid_cap ?? true,
        enable_age_constraint: config.enable_age_constraint ?? false,
        age_threshold: config.age_threshold || 19,
        minimum_age_count: config.minimum_age_count || 2,
        enable_owner_participation: config.enable_owner_participation ?? false,
        owner_flat_price_byc: config.owner_flat_price_byc || 0,
        enable_icon_players: config.enable_icon_players ?? false,
        icon_player_fixed_price_byc: config.icon_player_fixed_price_byc || 0,
        enable_bid_timer: config.enable_bid_timer ?? false,
        bid_timer_seconds: config.bid_timer_seconds || 30,
        enable_unsold_round: config.enable_unsold_round ?? true,
      });
      if (config.bid_increment_type === 'SLAB' && Array.isArray(config.bid_increment_value)) {
        setSlabs(config.bid_increment_value);
      }
    }
  }, [config]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlabChange = (index, field, value) => {
    const newSlabs = [...slabs];
    newSlabs[index][field] = parseFloat(value) || 0;
    setSlabs(newSlabs);
  };

  const addSlab = () => {
    const lastSlab = slabs[slabs.length - 1];
    setSlabs([...slabs, {
      min_amount: lastSlab?.max_amount || 0,
      max_amount: (lastSlab?.max_amount || 0) + 500000,
      increment: lastSlab?.increment || 50000
    }]);
  };

  const removeSlab = (index) => {
    if (slabs.length > 1) {
      setSlabs(slabs.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        bid_increment_value: formData.bid_increment_type === 'SLAB' ? slabs : formData.bid_increment_value
      };

      if (config) {
        await axiosInstance.patch(`/v1/events/${eventId}/auction/config`, payload);
        toast.success('Configuration updated');
      } else {
        await axiosInstance.post(`/v1/events/${eventId}/auction/config`, payload);
        toast.success('Configuration created');
      }
      onConfigChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Are you sure you want to lock this configuration? This will prevent further edits until a Super Admin unlocks it.')) {
      return;
    }
    try {
      setSubmitting(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/config/submit`, { confirm: true });
      toast.success('Configuration locked and submitted');
      onConfigChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to submit configuration'));
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-submit readiness checks
  const teamsRequired = formData.total_teams || 0;
  // Team count / squad sizes set in the tournament settings are read-only here
  // (the backend applies the tournament values anyway).
  const ec = eventDefaults?.event_config || {};
  const sizeLocked = config?.sizes_from_tournament || {
    total_teams: !!ec.max_teams, squad_size: !!ec.squad_max, minimum_squad_size: !!ec.squad_min,
  };
  const anySizeLocked = Object.values(sizeLocked).some(Boolean);
  const teamsReady    = teams.length >= teamsRequired && teamsRequired > 0;
  const poolReady     = poolStats ? (poolStats.pool_generated || poolStats.total_players > 0) : false;
  const canSubmit     = teamsReady && poolReady;

  return (
    <div className="space-y-6" data-testid="auction-config-panel">
      {/* Status Header */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">Auction Configuration</h3>
            <p className="text-sm text-gray-500">Configure auction rules and settings</p>
          </div>
          <div className="flex items-center gap-2">
            {config && (
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${AUCTION_STATUS_COLORS[config.auction_status] || 'bg-gray-100'}`}>
                {config.auction_status}
              </span>
            )}
            {isLocked && (
              <span className="flex items-center gap-1 text-amber-600 text-sm font-medium">
                <i className="ph-bold ph-lock"></i> Locked
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Team & Squad Settings */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold flex items-center gap-2">
            <i className="ph-bold ph-users-three text-[#3AAFA9]"></i>
            Team & Squad Settings
          </h4>
          {!config && eventDefaults?.source === 'event_config' && (
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full font-medium flex items-center gap-1">
              <i className="ph-bold ph-arrow-bend-down-left"></i>
              Inherited from event
            </span>
          )}
        </div>
        {anySizeLocked && (
          <p className="text-xs text-gray-500 mb-3" data-testid="sizes-from-tournament">
            <i className="ph-bold ph-lock-simple mr-1"></i>
            Greyed-out sizes come from the tournament settings — change them there (Edit event).
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Teams</label>
            <input
              type="number"
              value={formData.total_teams}
              onChange={(e) => handleChange('total_teams', parseInt(e.target.value))}
              disabled={!isEditable || sizeLocked.total_teams}
              min={2} max={20}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
              data-testid="config-total-teams"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Squad Size (Max)</label>
            <input
              type="number"
              value={formData.squad_size}
              onChange={(e) => handleChange('squad_size', parseInt(e.target.value))}
              disabled={!isEditable || sizeLocked.squad_size}
              min={5} max={25}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
              data-testid="config-squad-size"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Squad</label>
            <input
              type="number"
              value={formData.minimum_squad_size}
              onChange={(e) => handleChange('minimum_squad_size', parseInt(e.target.value))}
              disabled={!isEditable || sizeLocked.minimum_squad_size}
              min={1} max={formData.squad_size}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
              data-testid="config-min-squad"
            />
          </div>
        </div>
      </div>

      {/* BYC Wallet Settings */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <h4 className="font-bold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-wallet text-[#F4D341]"></i>
          BYC Wallet Settings
        </h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Initial Wallet (BYC)</label>
          <input
            type="number"
            value={formData.initial_wallet_byc}
            onChange={(e) => handleChange('initial_wallet_byc', parseFloat(e.target.value))}
            disabled={!isEditable}
            min={0}
            className="w-full max-w-md px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
            data-testid="config-initial-wallet"
          />
          <p className="text-xs text-gray-400 mt-1">Formatted: ₿ {formatBYC(formData.initial_wallet_byc)}</p>
        </div>
      </div>

      {/* Base Prices Section */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <h4 className="font-bold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-currency-circle-dollar text-green-500"></i>
          Base Prices
        </h4>
        <p className="text-xs text-gray-500 mb-4">Configure starting bid amounts for different player types</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Standard Player Base Price */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <i className="ph-bold ph-user text-blue-600 text-sm"></i>
              </div>
              <span className="text-sm font-bold text-gray-800">Standard Player</span>
            </div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Base Price (BYC)</label>
            <input
              type="number"
              value={formData.standard_base_price_byc}
              onChange={(e) => handleChange('standard_base_price_byc', parseFloat(e.target.value))}
              disabled={!isEditable}
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50 text-sm"
              data-testid="config-base-price"
            />
            <p className="text-xs text-gray-400 mt-1">₿ {formatBYC(formData.standard_base_price_byc)}</p>
          </div>

          {/* Icon Player Base Price */}
          <div className={`rounded-xl p-4 ${formData.enable_icon_players ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formData.enable_icon_players ? 'bg-purple-100' : 'bg-gray-200'}`}>
                  <i className={`ph-bold ph-star text-sm ${formData.enable_icon_players ? 'text-purple-600' : 'text-gray-400'}`}></i>
                </div>
                <span className="text-sm font-bold text-gray-800">Icon Player</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enable_icon_players}
                  onChange={(e) => handleChange('enable_icon_players', e.target.checked)}
                  disabled={!isEditable}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-500"></div>
              </label>
            </div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fixed Price (BYC)</label>
            <input
              type="number"
              value={formData.icon_player_fixed_price_byc}
              onChange={(e) => handleChange('icon_player_fixed_price_byc', parseFloat(e.target.value))}
              disabled={!isEditable || !formData.enable_icon_players}
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-100 disabled:text-gray-400 text-sm"
              data-testid="config-icon-price"
            />
            <p className="text-xs text-gray-400 mt-1">₿ {formatBYC(formData.icon_player_fixed_price_byc)}</p>
          </div>

          {/* Owner Player Base Price */}
          <div className={`rounded-xl p-4 ${formData.enable_owner_participation ? 'bg-orange-50' : 'bg-gray-50 opacity-60'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formData.enable_owner_participation ? 'bg-orange-100' : 'bg-gray-200'}`}>
                  <i className={`ph-bold ph-crown text-sm ${formData.enable_owner_participation ? 'text-orange-600' : 'text-gray-400'}`}></i>
                </div>
                <span className="text-sm font-bold text-gray-800">Owner Player</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enable_owner_participation}
                  onChange={(e) => handleChange('enable_owner_participation', e.target.checked)}
                  disabled={!isEditable}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Flat Price (BYC)</label>
            <input
              type="number"
              value={formData.owner_flat_price_byc}
              onChange={(e) => handleChange('owner_flat_price_byc', parseFloat(e.target.value))}
              disabled={!isEditable || !formData.enable_owner_participation}
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-100 disabled:text-gray-400 text-sm"
              data-testid="config-owner-price"
            />
            <p className="text-xs text-gray-400 mt-1">₿ {formatBYC(formData.owner_flat_price_byc)}</p>
          </div>
        </div>
      </div>

      {/* Bidding Rules */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <h4 className="font-bold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-chart-line-up text-[#E54562]"></i>
          Bidding Rules
        </h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Increment Type</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="bid_increment_type"
                value="FIXED"
                checked={formData.bid_increment_type === 'FIXED'}
                onChange={() => handleChange('bid_increment_type', 'FIXED')}
                disabled={!isEditable}
                className="text-[#3AAFA9] focus:ring-[#3AAFA9]"
              />
              <span className="text-sm">Fixed Increment</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="bid_increment_type"
                value="SLAB"
                checked={formData.bid_increment_type === 'SLAB'}
                onChange={() => handleChange('bid_increment_type', 'SLAB')}
                disabled={!isEditable}
                className="text-[#3AAFA9] focus:ring-[#3AAFA9]"
              />
              <span className="text-sm">Slab-based Increment</span>
            </label>
          </div>
        </div>

        {formData.bid_increment_type === 'FIXED' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Increment (BYC)</label>
            <input
              type="number"
              value={formData.bid_increment_value}
              onChange={(e) => handleChange('bid_increment_value', parseFloat(e.target.value))}
              disabled={!isEditable}
              min={0}
              className="w-full max-w-xs px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
              data-testid="config-fixed-increment"
            />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Slab Configuration</label>
              {isEditable && (
                <button
                  onClick={addSlab}
                  className="text-sm text-[#3AAFA9] hover:text-[#2d8a85] font-medium"
                  data-testid="add-slab-btn"
                >
                  + Add Slab
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {slabs.map((slab, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Min Amount</label>
                      <input
                        type="number"
                        value={slab.min_amount}
                        onChange={(e) => handleSlabChange(index, 'min_amount', e.target.value)}
                        disabled={!isEditable}
                        className="w-full px-2 py-1 rounded border border-gray-200 text-sm disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Max Amount</label>
                      <input
                        type="number"
                        value={slab.max_amount}
                        onChange={(e) => handleSlabChange(index, 'max_amount', e.target.value)}
                        disabled={!isEditable}
                        className="w-full px-2 py-1 rounded border border-gray-200 text-sm disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Increment</label>
                      <input
                        type="number"
                        value={slab.increment}
                        onChange={(e) => handleSlabChange(index, 'increment', e.target.value)}
                        disabled={!isEditable}
                        className="w-full px-2 py-1 rounded border border-gray-200 text-sm disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                  {isEditable && slabs.length > 1 && (
                    <button
                      onClick={() => removeSlab(index)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <i className="ph-bold ph-trash"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enable_max_bid_cap}
              onChange={(e) => handleChange('enable_max_bid_cap', e.target.checked)}
              disabled={!isEditable}
              className="rounded text-[#3AAFA9] focus:ring-[#3AAFA9]"
            />
            <span className="text-sm font-medium">Enable Max Bid Cap</span>
          </label>
          <p className="text-xs text-gray-400 ml-6">Prevents teams from overbidding based on remaining slots</p>
        </div>
      </div>

      {/* Age Constraints */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold flex items-center gap-2">
            <i className="ph-bold ph-user-circle text-[#2B3A67]"></i>
            Age Constraints
          </h4>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enable_age_constraint}
              onChange={(e) => handleChange('enable_age_constraint', e.target.checked)}
              disabled={!isEditable}
              className="rounded text-[#3AAFA9] focus:ring-[#3AAFA9]"
            />
            <span className="text-sm font-medium">Enable</span>
          </label>
        </div>
        {formData.enable_age_constraint && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Threshold (Under)</label>
              <input
                type="number"
                value={formData.age_threshold}
                onChange={(e) => handleChange('age_threshold', parseInt(e.target.value))}
                disabled={!isEditable}
                min={1} max={100}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Players Required</label>
              <input
                type="number"
                value={formData.minimum_age_count}
                onChange={(e) => handleChange('minimum_age_count', parseInt(e.target.value))}
                disabled={!isEditable}
                min={0}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
              />
            </div>
          </div>
        )}
      </div>

      {/* Timer & Unsold Settings */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <h4 className="font-bold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-timer text-[#E54562]"></i>
          Timer & Round Settings
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.enable_bid_timer}
                onChange={(e) => handleChange('enable_bid_timer', e.target.checked)}
                disabled={!isEditable}
                className="rounded text-[#3AAFA9] focus:ring-[#3AAFA9]"
              />
              <span className="text-sm font-medium">Enable Bid Timer</span>
            </label>
            {formData.enable_bid_timer && (
              <div className="ml-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Timer Duration (seconds)</label>
                <input
                  type="number"
                  value={formData.bid_timer_seconds}
                  onChange={(e) => handleChange('bid_timer_seconds', parseInt(e.target.value))}
                  disabled={!isEditable}
                  min={5} max={300}
                  className="w-full max-w-xs px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none disabled:bg-gray-50"
                />
              </div>
            )}
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enable_unsold_round}
              onChange={(e) => handleChange('enable_unsold_round', e.target.checked)}
              disabled={!isEditable}
              className="rounded text-[#3AAFA9] focus:ring-[#3AAFA9]"
            />
            <span className="text-sm font-medium">Enable Unsold Round</span>
          </label>
        </div>
      </div>

      {/* Auctioneer Credentials - Show whenever config exists (any status including DRAFT) */}
      {config && (
        <AuctioneerCredentialsSection eventId={eventId} />
      )}

      {/* Action Buttons */}
      {isEditable && (
        <div className="space-y-3 sticky bottom-0 bg-gray-50 py-4 -mx-6 px-6 border-t border-gray-200">
          {/* Pre-submit checklist — shown when Lock & Submit is relevant */}
          {config && (
            <div className={`rounded-xl p-3 border text-xs space-y-1.5 ${canSubmit ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`font-bold mb-1 ${canSubmit ? 'text-green-800' : 'text-amber-800'}`}>
                {canSubmit ? 'Ready to lock' : 'Complete before locking:'}
              </p>
              <div className="flex items-center gap-2">
                <i className={`ph-bold text-base ${teamsReady ? 'ph-check-circle text-green-500' : 'ph-x-circle text-amber-500'}`}></i>
                <span className={teamsReady ? 'text-green-700' : 'text-amber-700'}>
                  Teams: {teams.length}/{teamsRequired} created
                </span>
              </div>
              <div className="flex items-center gap-2">
                <i className={`ph-bold text-base ${poolReady ? 'ph-check-circle text-green-500' : 'ph-x-circle text-amber-500'}`}></i>
                <span className={poolReady ? 'text-green-700' : 'text-amber-700'}>
                  Player pool: {poolReady ? `${poolStats?.total_players || 0} players generated` : 'Not generated yet'}
                </span>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-[#2B3A67] text-white font-bold hover:bg-[#1e2a4a] transition-colors disabled:opacity-50"
              data-testid="save-config-btn"
            >
              {saving ? 'Saving...' : (config ? 'Update Configuration' : 'Create Configuration')}
            </button>
            {config && (
              <button
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                title={!canSubmit ? 'Create all teams and generate player pool first' : 'Lock configuration'}
                className="flex-1 py-3 rounded-xl bg-[#3AAFA9] text-white font-bold hover:bg-[#2d8a85] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-config-btn"
              >
                {submitting ? 'Submitting...' : 'Lock & Submit'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
